import { useMemo, useCallback } from 'react';

const getSortDate = (order) => {
  if (order.downloadLink) {
    // For monthly summaries, use createdAt
    return new Date(order.createdAt);
  } else {
    // For regular orders, parse date from orderId (format: YYMMDDxxx)
    const id = order.orderId?.toString() || "";
    if (id.length >= 6) {
      const yy = parseInt(id.substring(0, 2), 10);
      const mm = parseInt(id.substring(2, 4), 10) - 1;
      const dd = parseInt(id.substring(4, 6), 10);
      const year = 2000 + yy;
      const date = new Date(year, mm, dd);
      if (isNaN(date.getTime())) {
        return new Date(order.createdAt);
      }
      return date;
    } else {
      // Fallback to createdAt if orderId format is invalid
      return new Date(order.createdAt);
    }
  }
};

export const useRevenueData = (data, timeFrame, selectedDate, selectedMonth, institution) => {
  return useMemo(() => {
    if (!data) return null;

    // Filter valid orders and separate regular orders from monthly summaries
    const validOrders = data.filter(order => order.paymentStatus === true);
    const regularOrders = validOrders.filter(order => !order.downloadLink);
    const monthlySummaries = validOrders.filter(order => order.downloadLink);

    const filterData = (orders) => {
      switch (timeFrame) {
        case 'today': {
          const filterDate = selectedDate || new Date();
          const filterYear = filterDate.getFullYear();
          const filterMonth = filterDate.getMonth();
          const filterDay = filterDate.getDate();
          return orders.filter(order => {
            const orderDate = getSortDate(order);
            return !isNaN(orderDate.getTime()) &&
              orderDate.getFullYear() === filterYear &&
              orderDate.getMonth() === filterMonth &&
              orderDate.getDate() === filterDay;
          });
        }
        case 'monthly': {
          const filterDate = selectedMonth || new Date();
          const filterYear = filterDate.getFullYear();
          const filterMonth = filterDate.getMonth();
          return orders.filter(order => {
            const orderDate = getSortDate(order);
            return !isNaN(orderDate.getTime()) &&
              orderDate.getMonth() === filterMonth &&
              orderDate.getFullYear() === filterYear;
          });
        }
        default:
          return orders;
      }
    };

    const filteredRegularOrders = filterData(regularOrders);
    const filteredMonthlySummaries = filterData(monthlySummaries);
    const filteredOrders = [...filteredRegularOrders, ...filteredMonthlySummaries];

    // Helper function to check if payment method is complementary
    const isComplementaryPayment = (paymentMethod) => {
      if (!paymentMethod) return false;
      return paymentMethod.toLowerCase().includes('complementary') || 
             paymentMethod.toLowerCase().includes('complimentary');
    };

    // Helper function to get revenue amount excluding complementary payments
    const getRevenueFromOrder = (order) => {
      if (order.downloadLink) {
        // Monthly summary record - check paymentMethods object
        if (order.paymentMethods && typeof order.paymentMethods === 'object') {
          return Object.entries(order.paymentMethods).reduce((sum, [method, amount]) => {
            if (isComplementaryPayment(method)) {
              return sum; // Don't add complementary amount to revenue
            }
            return sum + (Number(amount) || 0);
          }, 0);
        } else {
          // Monthly summary without paymentMethods breakdown - check if order itself is complementary
          const orderPaymentMethod = order.paymentMethod || order.paymentMethodType || order.paymentType;
          if (isComplementaryPayment(orderPaymentMethod)) {
            return 0;
          }
          return order.totalAmount || 0;
        }
      } else {
        // Regular order - handle both single payment method and array of payment methods
        if (Array.isArray(order.paymentMethods)) {
          // Handle array of payment methods
          return order.paymentMethods.reduce((sum, methodEntry) => {
            if (methodEntry?.method && typeof methodEntry.amount === "number") {
              if (isComplementaryPayment(methodEntry.method)) {
                return sum; // Don't add complementary amount to revenue
              }
              return sum + methodEntry.amount;
            }
            return sum;
          }, 0);
        } else {
          // Handle single payment method
          let paymentMethod = order.paymentMethod || order.paymentMethodType || order.paymentType;
          if (isComplementaryPayment(paymentMethod)) {
            return 0; // Don't add complementary orders to revenue
          }
          return order.totalAmount || 0;
        }
      }
    };

    // Calculate revenue excluding complementary payments
    const totalRevenue = filteredOrders.reduce((sum, order) => {
      return sum + getRevenueFromOrder(order);
    }, 0);

    // Calculate total orders (including complementary for count)
    const totalOrders = filteredOrders.reduce((sum, order) => {
      if (order.downloadLink && order.totalOrders !== undefined) {
        // Monthly summary record - use the totalOrders field
        return sum + Number(order.totalOrders);
      }
      // Regular order - count all orders including complementary
      return sum + 1;
    }, 0);

    // Calculate average order value based on revenue (excluding complementary)
    const revenueGeneratingOrders = filteredOrders.reduce((count, order) => {
      if (order.downloadLink) {
        // For monthly summaries, we need to estimate non-complementary orders
        // This is complex without detailed breakdown, so we'll use totalOrders as approximation
        return count + (Number(order.totalOrders) || 0);
      } else {
        // Regular order - check if it's not complementary
        let paymentMethod = order.paymentMethod || order.paymentMethodType || order.paymentType;
        if (Array.isArray(order.paymentMethods)) {
          // If any payment method in the array is non-complementary, count the order
          const hasNonComplementary = order.paymentMethods.some(methodEntry => 
            !isComplementaryPayment(methodEntry?.method)
          );
          return hasNonComplementary ? count + 1 : count;
        } else {
          return isComplementaryPayment(paymentMethod) ? count : count + 1;
        }
      }
    }, 0);

    const averageOrderValue = revenueGeneratingOrders > 0 ? totalRevenue / revenueGeneratingOrders : 0;

    // Calculate total tax only on revenue-generating orders
    const totalTax = institution === "bhuk2323" 
      ? totalRevenue * 0.05  
      : filteredOrders.reduce((sum, order) => {
          // Only calculate tax if the order contributes to revenue
          const orderRevenue = getRevenueFromOrder(order);
          if (orderRevenue === 0) return sum; // Skip complementary orders for tax calculation
          
          if (order.downloadLink) {
            // Monthly summary record - tax is already calculated
            const cgst = isNaN(parseFloat(order.cgst)) ? 0 : parseFloat(order.cgst);
            const sgst = isNaN(parseFloat(order.sgst)) ? 0 : parseFloat(order.sgst);
            const serviceTax = isNaN(parseFloat(order.serviceTax)) ? 0 : parseFloat(order.serviceTax);
            return sum + cgst + sgst + serviceTax;
          } else {
            // Regular order
            const cgst = isNaN(parseFloat(order.cgst)) ? 0 : parseFloat(order.cgst);
            const sgst = isNaN(parseFloat(order.sgst)) ? 0 : parseFloat(order.sgst);
            const serviceTax = isNaN(parseFloat(order.serviceTax)) ? 0 : parseFloat(order.serviceTax);
            return sum + cgst + sgst + serviceTax;
          }
        }, 0);

    // Get unique dates and months from all orders (including summaries)
    const dateStrings = validOrders.map(order => {
      const date = getSortDate(order);
      if (isNaN(date.getTime())) return null;
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }).filter(Boolean);
    const uniqueDates = [...new Set(dateStrings)].sort((a, b) => b.localeCompare(a));

    const monthStrings = validOrders.map(order => {
      const date = getSortDate(order);
      if (isNaN(date.getTime())) return null;
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }).filter(Boolean);
    const uniqueMonths = [...new Set(monthStrings)].sort((a, b) => b.localeCompare(a));

    // Generate hourly data from regular orders (excluding complementary revenue)
    // Note: Hour is still from createdAt since orderId doesn't contain hour info
    const hourlyData = Array.from({ length: 24 }, (_, i) => ({
      hour: `${i.toString().padStart(2, '0')}:00`,
      revenue: 0,
      orders: 0,
    }));
    
    filteredRegularOrders.forEach(order => {
      const orderDate = new Date(order.createdAt);
      const hour = orderDate.getHours();
      
      // Count all orders including complementary
      hourlyData[hour].orders += 1;
      
      // Add revenue only if not complementary
      const orderRevenue = getRevenueFromOrder(order);
      hourlyData[hour].revenue += orderRevenue;
    });

    // Calculate chart data
    const chartData = timeFrame === "today" ? hourlyData : 
      filteredOrders.reduce((acc, order) => {
        const date = getSortDate(order);
        if (isNaN(date.getTime())) return acc;
        const key = timeFrame === "monthly" 
          ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        if (!acc[key]) {
          acc[key] = { date: key, revenue: 0, orders: 0 };
        }
        
        // Handle both regular orders and monthly summaries
        if (order.downloadLink) {
          // Monthly summary
          acc[key].revenue += getRevenueFromOrder(order);
          acc[key].orders += order.totalOrders || 0;
        } else {
          // Regular order
          acc[key].revenue += getRevenueFromOrder(order);
          acc[key].orders += 1;
        }
        
        return acc;
      }, {});

    const finalChartData = timeFrame === "today" ? hourlyData : 
      Object.values(chartData).sort((a, b) => a.date.localeCompare(b.date)).slice(-30);

    return {
      summary: {
        totalRevenue,
        totalOrders,
        averageOrderValue,
        totalTax,
      },
      hourlyData,
      uniqueDates,
      uniqueMonths,
      filteredOrders,
      regularOrders: filteredRegularOrders,
      monthlySummaries: filteredMonthlySummaries,
      chartData: finalChartData,
    };
  }, [data, timeFrame, selectedDate, selectedMonth, institution]);
};